import { breakpoints, Heading, Stack } from '@etg/wings';
import { css, keyframes } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { Dispatch, SetStateAction } from 'react';
import type { RebookEligibilityType } from '../../order-details/components/manage-trip/rebooking/models';
import type { RebookingSearchType } from '../models';
import NoResults from './NoResults';
import RebookingTripResult from './RebookingTripResult';

const resultsMoveAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translateY(0);
  }
  10% {
    opacity: 0.5;
    transform: translateY(10px);
  }
  20% {
    opacity: 0.25;
    transform: translateY(20px);
  }
  30% {
    opacity: 0.25;
    transform: translateY(30px);
  }
  40% {
    opacity: 0.25;
    transform: translateY(40px);
  }
  50% {
    opacity: 0;
    transform: translateY(50px);
  }
  60% {
    opacity: 0.25;
    transform: translateY(35px);
  }
  70% {
    opacity: 0.5;
    transform: translateY(15px);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const flightResultsInnerWrapperStyles = css`
  animation: ${resultsMoveAnimation} 0.8s;
`;

interface AvailableTripResultsProps {
  rebookEligibilityType: RebookEligibilityType;
  search: RebookingSearchType;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  shouldFetchMoreResults: boolean;
}

const AvailableTripResults = ({
  rebookEligibilityType,
  search,
  setFetchMoreResults,
  shouldFetchMoreResults,
}: AvailableTripResultsProps) => {
  const { t } = useTranslation();

  if (!search) {
    return null;
  }
  const { flights, flightsCount, hasAirlineAncillaries } = search;

  return (
    <Stack
      spacing={{
        [breakpoints._0]: 16,
        [breakpoints._768]: 24,
      }}
    >
      <Heading level={2}>{t('ChangeTrip.Results.Title')}</Heading>
      <Stack data-testid="changeTrip-searchResults">
        <Stack className={flightResultsInnerWrapperStyles}>
          {flights.map((flight, index) => (
            <RebookingTripResult
              flight={flight}
              hasAirlineAncillaries={hasAirlineAncillaries}
              index={index}
              key={index}
              rebookEligibilityType={rebookEligibilityType}
            />
          ))}
        </Stack>
        <NoResults
          flightResults={flights}
          flightsCount={flightsCount}
          setFetchMoreResults={setFetchMoreResults}
          shouldFetchMoreResults={shouldFetchMoreResults}
        />
      </Stack>
    </Stack>
  );
};

export default AvailableTripResults;
